import React, { useState, useEffect, useRef } from "react";
import Cookies from "js-cookie";
import dynamic from "next/dynamic";
const Paystack = dynamic(() => import("../common/Paystack"), { ssr: false });

import {
  getLoggedinUser,
  useActiveCart,
  useAuth,
  useCartCancelPayment,
  useCartPayment,
  useCartSignature,
  useCheckUserMembership,
  useCloseCart,
  useGetDestinations,
  useIpDetails,
  useProcessCart,
  useUpdateCart,
} from "../../lib/util";
import toast from "react-hot-toast";
import AuthForm from "../auth/AuthForm";
import ThankYou from "../common/ThankYou";
import CartCheckout from "./cart-checkout";

import DeliveryMethod from "./delivery-method";
import FinalCheckout from "./final-checkout";
import UserInfo from "./userInfo";
import { getActiveUser, logOutUser, logTempToken } from "../slice/auth-slice";
import { useAppDispatch, useAppSelector } from "../slice/hooks";
import { updateCart } from "../slice/cart-slice";

const CartModal = ({
  cart,
  showProduct,
  closeModal,
  accessPoint,
  activateBackButton,
  checkoutState,
  setCheckoutState,
  publicUser,
  setPublicUser,
  isPageMode,
  storePath,
  organisation,
}) => {
  const dispatch = useAppDispatch();
  const [error, setError] = useState("");
  const [isMember, setIsMember] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [thankYouData, setThankYouData] = useState({});

  const { loggedInUser } = useAppSelector((state) =>
    getActiveUser(state, getLoggedinUser())
  );

  const { logUserOut } = useAuth();

  const [cartItems, setCartItems] = useState({
    ...cart,
    paymentItem: cart?.paymentItem ?? [],
  });

  useEffect(() => {
    if (cart) {
      setCartItems(cart);
    }
  }, [cart?.collectionId]);

  const { data: ipDetails, isSuccess, isError } = useIpDetails();
  const [deliveryRegion, setDeliveryRegion] = useState(null);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState("");

  const [confirmation, setConfirmation] = useState(false);

  const pushCartUpdate = (cart) => {
    if (cart) {
      dispatch(updateCart(cart.profileId, cart.accessPointId, cart));
      setCartItems(cart);
    }
  };

  const { isLoading: isCartLoading, mutate: mutateActiveCart } = useActiveCart({
    onError: (response) => {
      setError(response.message);
    },
    onSuccess: async (response) => {
      pushCartUpdate(response.data?.collection);
    },
  });

  const {
    mutate: mutateFetchDeliveryRegion,
    isLoading: isLoadingDeliveryRegion,
  } = useGetDestinations({
    onSuccess: (res) => {
      setDeliveryRegion(res.data.destinations);
    },
    onError: (error) => {
      console.error("Error fetching :", error.message);
    },
  });

  useEffect(() => {
    if (cartItems && cartItems?.accessPointId) {
      mutateFetchDeliveryRegion({
        orgId: cartItems?.host?.profileId,
        accessPointId: cartItems?.accessPointId,
      });
    }
  }, [cartItems?.accessPointId]);

  const [paymentInfo, setPaymentInfo] = useState({
    amount: 0,
    email: "",
    reference: "",
    publicKey: "",
    transaction_charge: 0,
  });

  const { isLoadingCartSignature, mutate: mututateCartSignature } =
    useCartSignature({
      onError: (response) => {
        setError(response.message);
      },
      onSuccess: async (response) => {
        pushCartUpdate(response.data?.collection);
        toast.success("Cart Updated");
      },
    });

  const { isLoadingCartCancelPayment, mutate: mututateCartCancelPayment } =
    useCartCancelPayment({
      onError: (response) => {
        setError(response.message);
      },
      onSuccess: async (response) => {
        const cart = response.data.collection;
        pushCartUpdate(response.data?.collection);
        mutateActiveCart({
          orgId: cart.host.profileId,
          accessPointId: cart.accessPointId,
        });
      },
    });

  const { isLoading: isLoadingPaid, mutate: mutateInitializePayment } =
    useCartPayment({
      onError: (response) => {
        setCheckoutState("cartItems");
        setError(response.message);
      },
      onSuccess: async (response) => {
        const {
          access,
          paymentNo,
          amountToCharge,
          transactionEmail,
          transactionCharge,
        } = response.data;
        setPaymentInfo({
          amount: amountToCharge,
          email: transactionEmail,
          reference: paymentNo,
          publicKey: access.key,
          transaction_charge: transactionCharge,
          channels: ["card"],
        });
      },
    });

  const {
    isLoading: isLoadingCheckUserMembership,
    mutate: mutateCheckUserMembership,
  } = useCheckUserMembership({
    onError: (response) => {},
    onSuccess: async (response) => {
      setIsMember(response.data.member);
    },
  });

  const { isLoading: isLoadingProcessOrder, mutate: mutateProcessOrder } =
    useProcessCart({
      onError: (response) => {
        setCheckoutState("cartItems");
        setError(response.message);
        pushCartUpdate(response.data?.collection);
      },
      onSuccess: async (response) => {
        pushCartUpdate(response.data?.collection);

        const cart = response.data.collection;
        const email = cart.publicOrder
          ? cart.publicInfo?.email
          : cart.author.email;

        setThankYouData({ user: { email } });

        const amountZero = Number(cart.amount || 0) === 0;

        const isInactiveInstant =
          cart.status === "INACTIVE" && cart.collectionType === "INSTANT";

        const isManualPayment =
          cart.paymentMode === "BANK_TRANSFER" ||
          cart.paymentMode === "NO_PAYMENT" ||
          cart.paymentMode === "PAY_ON_DELIVERY";

        const shouldCloseCart =
          amountZero || isInactiveInstant || isManualPayment;

        if (shouldCloseCart) {
          setCheckoutSuccess(true);
          mutateCloseCart({
            orgId: cart?.profileId,
            accessPointId: cart?.accessPointId,
          });
        } else {
          const orderData = {
            collectionId: cart.collectionId,
            mode: "ONLINE",
            gateWay: "PAYSTACK",
            channel: "WEB",
            amount: cart.totalAmount,
          };
          if (!cart.publicOrder && cart.userId) {
            orderData.userId = cart.userId;
          }
          mutateInitializePayment(orderData);
        }
      },
    });

  const { isLoadingUpdate, mutate: mututateUpdateCart } = useUpdateCart({
    onError: (response) => {
      setError(response.message);
      pushCartUpdate(response.data?.collection);
    },
    onSuccess: async (response) => {
      setCheckoutState("cartItems");
      pushCartUpdate(response.data?.collection);
      setConfirmation(true);
    },
  });

  const { isLoading: isLoadingGetCart, mutate: mutateCloseCart } = useCloseCart(
    {
      onError: (response) => {
        setError(response.message);
      },
      onSuccess: async (response) => {
        pushCartUpdate(response.data?.collection);
      },
    }
  );

  const handlePaystackSuccess = () => {
    setPaymentInfo({
      amount: 0,
      email: "",
      reference: "",
      publicKey: "",
      transaction_charge: 0,
    });
    setCheckoutSuccess(true);

    mutateCloseCart({
      orgId: cartItems.profileId,
      accessPointId: cartItems?.accessPointId,
    });
  };

  const handlePaystackClose = () => {
    setCheckoutState("cartItems");
    setPaymentInfo({
      amount: 0,
      email: "",
      reference: "",
      publicKey: "",
      transaction_charge: 0,
    });
    mututateCartCancelPayment({
      collectionId: cartItems.collectionId,
    });
  };

  const handleLoginClick = () => {
    setCheckoutState("authentication");
  };

  const handleLogout = async () => {
    dispatch(logOutUser());
    const geo = isSuccess ? ipDetails : null;
    const { token, tokenExpiryDate } = await logUserOut(geo);
    dispatch(
      logTempToken({
        token,
        tokenExpiryDate,
      })
    );
  };

  const handleBackToCart = () => {
    setCheckoutState("cartItems");
  };

  const handleCartLogin = (user) => {
    mututateCartSignature({
      collectionId: cartItems.collectionId,
      token: user.token,
    });
  };

  const gotoCart = () => {
    setError("");
    setConfirmation(false);
    setCheckoutState("cartItems");
  };

  const gotoDelivery = () => {
    setError("");
    setConfirmation(false);
    setCheckoutState("delivery");
  };

  const gotoContact = () => {
    setError("");
    setConfirmation(false);
    setCheckoutState("userdetails");
  };

  const handleCartSubmit = () => {
    setError("");
    const paymentItem = cartItems.paymentItem || [];
    if (paymentItem.length === 0) {
      setError("Please select at least one item.");
      return;
    }

    if ((!loggedInUser || !loggedInUser.user) && !publicUser) {
      setCheckoutState("userdetails");
      return;
    }
    setCheckoutState("delivery");
    return;
  };

  const handlePublicUser = (user) => {
    setPublicUser(user);
    setDeliveryInfo((prev) => ({
      ...prev,
      phone: user.phone, // <-- prefill phone
    }));
    setCheckoutState("delivery");
  };

  const handleDelivery = (deliveryInfo, deliveryMode) => {
    if (!loggedInUser?.user && !publicUser) {
      setError("No valid user found for order processing.");
      return;
    }

    const { deliveryRegion = "" } = deliveryInfo;
    const [region, amount] = deliveryRegion.split("-");
    const deliveryAmount = parseFloat(amount) || 0;

    const processData = {
      orgId: cartItems?.host?.profileId,
      accessPointId: cartItems?.accessPointId,
      deliveryMode,
    };

    let updatedDeliveryInfo = { ...deliveryInfo };

    switch (deliveryMode) {
      case "LOCATION":
        updatedDeliveryInfo.deliveryRegion = region;
        if (deliveryAmount) {
          processData.deliveryAmount = deliveryAmount;
        }
        break;

      case "PICKUP":
        updatedDeliveryInfo.pickUpAddress = accessPoint?.address;
        break;

      case "INSTORE":
        // no change needed
        break;

      default:
        setError("Invalid delivery mode selected.");
        return;
    }

    setDeliveryInfo(updatedDeliveryInfo);
    processData.deliveryInfo = updatedDeliveryInfo;

    if (loggedInUser?.user) {
      processData.userId = loggedInUser.user.userId;
    } else {
      const { firstName, lastName, email, phone, intCode } = publicUser;
      processData.publicInfo = { firstName, lastName, email, phone, intCode };
    }
    setCheckoutState("processed");
    mututateUpdateCart(processData);
  };

  const handlePayment = (paymentType, documents = null) => {
    const processData = {
      orgId: cartItems?.host?.profileId,
      accessPointId: cartItems?.accessPointId,
    };

    const paymentModes = {
      ONLINE: "ONLINE",
      BANK_TRANSFER: "BANK_TRANSFER",
      NO_PAYMENT: "NO_PAYMENT",
    };

    const selectedPaymentMode = paymentModes[paymentType];

    if (!selectedPaymentMode) {
      setError(`Unsupported payment type: ${paymentType}`);
      return;
    }

    processData.paymentMode = selectedPaymentMode;

    if (paymentType === "BANK_TRANSFER" && documents) {
      processData.supportingDocuments = documents;
    }
    setCheckoutState("processed");

    mutateProcessOrder(processData);
  };

  useEffect(() => {
    setCheckoutState("cartItems");

    if (loggedInUser && loggedInUser.user) {
      const storedUserId = Cookies.get("checkedUserId");
      if (storedUserId === loggedInUser.user.userId) {
        // If the user ID is the same, do not rerun
        return;
      }
      // Store the new user ID in cookies
      Cookies.set("checkedUserId", loggedInUser.user.userId, { expires: 7 });
      // Run the membership check
      mutateCheckUserMembership({
        userId: loggedInUser.user.userId,
        orgId: cartItems.host.profileId,
      });
    }
  }, [loggedInUser]);

  return (
    <>
      <Paystack
        paymentInfo={paymentInfo}
        onSuccessPayment={handlePaystackSuccess}
        onClosePayment={handlePaystackClose}
      />
      {checkoutSuccess ? (
        <ThankYou
          host={cartItems?.host}
          loggedInUser={loggedInUser}
          data={thankYouData}
          isMember={isMember}
        />
      ) : (
        <div className="cart">
          {checkoutState == "cartItems" && (
            <CartCheckout
              showProduct={showProduct}
              closeModal={closeModal}
              setError={setError}
              handleLoginClick={handleLoginClick}
              handleCartSubmit={handleCartSubmit}
              cart={cartItems}
              error={error}
              cartProcessLoading={isLoadingProcessOrder || isLoadingPaid}
              confirmation={confirmation}
              activateBackButton={activateBackButton}
              onProceed={handlePayment}
              accessPoint={accessPoint}
              gotoCart={gotoCart}
              gotoDelivery={gotoDelivery}
              gotoContact={gotoContact}
              isPageMode={isPageMode}
              storePath={storePath}
              organisation={organisation}
            />
          )}

          {checkoutState === "authentication" && (
            <>
              <AuthForm
                onBack={handleBackToCart}
                onLogin={handleCartLogin}
                defaultLogin={false}
                activateBackButton={activateBackButton}
              />
            </>
          )}

          {checkoutState === "userdetails" && (
            <>
              <UserInfo
                onBack={handleBackToCart}
                onSuccess={handlePublicUser}
                handleLogout={handleLogout}
                publicUser={publicUser}
                activateBackButton={activateBackButton}
                setCheckoutState={setCheckoutState}
                cart={cartItems}
              />
            </>
          )}

          {checkoutState === "delivery" && (
            <>
              <DeliveryMethod
                onBack={handleBackToCart}
                accessPoint={accessPoint}
                onSuccess={handleDelivery}
                deliveryInfo={deliveryInfo}
                deliveryRegion={deliveryRegion}
                activateBackButton={activateBackButton}
                selectedMethod={selectedDeliveryMethod}
                setSelectedMethod={setSelectedDeliveryMethod}
              />
            </>
          )}

          {checkoutState === "processed" && (
            <>
              <FinalCheckout
                cartProcessLoading={
                  isLoadingProcessOrder ||
                  isLoadingPaid ||
                  isLoadingCartCancelPayment ||
                  isLoadingUpdate
                }
              />
            </>
          )}
        </div>
      )}
    </>
  );
};

export default CartModal;
